import React from "react"
import Search from "../search"
import { InlineIndicator } from "../loader"
import { Pagination, DEFAULT_PAGE_SIZE } from "./Pagination"

/**
 * @typedef {Object} Column
 * @property {string} key - Column data key
 * @property {string} label - Column header label
 */

/**
 * @typedef {Object} PaginatedTableProps
 * @property {Array<Column>} columns - Column definitions
 * @property {Array<Object>} data - Current page of row data
 * @property {number} totalCount - Total rows (for pagination math)
 * @property {number} page - Current page (0-indexed)
 * @property {number} pageSize - Rows per page
 * @property {function(number): void} onPageChange - Page change callback
 * @property {function(number): void} onPageSizeChange - Page size change callback
 * @property {string|null} searchValue - Current search query (null = no search)
 * @property {function(string|null): void} onSearch - Search callback
 * @property {function(Object, string): React.ReactNode} [renderCell] - Custom cell renderer
 * @property {string} [rowKey] - Property name to use as row key (defaults to "id")
 * @property {boolean} [isLoading] - Loading state
 * @property {string} [emptyMessage] - Message when no data
 * @property {string} [noResultsMessage] - Message when search has no results
 */

/**
 * Generic paginated table component with search.
 * @param {PaginatedTableProps} props
 */
export function PaginatedTable({
  columns,
  data,
  totalCount,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  searchValue,
  onSearch,
  renderCell,
  rowKey = "id",
  isLoading = false,
  emptyMessage = "No data available",
  noResultsMessage = "No results found"
}) {
  const hasData = data.length > 0
  const hasSearchQuery = searchValue != null && searchValue !== ""
  const showEmptyMessage = !isLoading && !hasData && !hasSearchQuery
  const showNoResults = !isLoading && !hasData && hasSearchQuery
  const showPagination = !isLoading && totalCount > 0

  const renderCellContent = (row, column) => {
    if (renderCell) {
      return renderCell(row, column.key)
    }
    return row[column.key]
  }

  return (
    <div className="paginated-table">
      {!showEmptyMessage && (
        <Search
          className="paginated-table__search"
          search={searchValue}
          onSearch={onSearch}
          disabled={isLoading}
        />
      )}

      <div className="paginated-table__content">
        {isLoading && (
          <div className="paginated-table__loading">
            <InlineIndicator show={true} />
          </div>
        )}

        {showEmptyMessage && (
          <div className="paginated-table__empty">
            {emptyMessage}
          </div>
        )}

        {showNoResults && (
          <div className="paginated-table__empty">
            {noResultsMessage}
          </div>
        )}

        {!isLoading && hasData && (
          <table className="paginated-table__table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.map((row, idx) => (
                <tr key={row[rowKey] || idx}>
                  {columns.map((col) => (
                    <td key={col.key}>
                      {renderCellContent(row, col)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showPagination && (
        <Pagination
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </div>
  )
}
